<template>
  <footer class="app-footer">
    <div class="app-footer__inner">
      <div class="app-footer__left">
        <span class="app-footer__copy">{{ copyright }}</span>
        <span v-if="version" class="app-footer__sep">·</span>
        <span v-if="version" class="app-footer__version">{{ version }}</span>
      </div>
      <div v-if="$slots.right || supportEmail" class="app-footer__right">
        <slot name="right">
          <a
            v-if="supportEmail"
            :href="`mailto:${supportEmail}`"
            class="app-footer__link"
          >
            <i class="pi pi-envelope" aria-hidden="true" />
            {{ supportEmail }}
          </a>
        </slot>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
/**
 * App footer — minimal strip with copyright + version on the left and
 * a support / link cluster on the right. Renders the same on login and
 * in-app, so users always know what version they're on and where to
 * get help.
 *
 * Generic by design — `@ram/ui` doesn't know about i18n, so the
 * consumer passes pre-translated strings in.
 */
defineProps<{
  /** "© 2026 Rafid Auto-Maintenance" — usually built from i18n. */
  copyright: string;
  /** "v0.2.0" or similar; falsy hides the version slot. */
  version?: string;
  /** Email address — renders as a mailto link in the right slot. */
  supportEmail?: string;
}>();
</script>

<style scoped>
.app-footer {
  border-top: 1px solid var(--ram-color-border);
  background: var(--ram-color-surface);
  font-size: 12px;
  color: var(--ram-color-text-muted);
  flex-shrink: 0;
}
.app-footer__inner {
  max-width: 1400px;
  margin: 0 auto;
  padding: 12px var(--ram-space-6);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ram-space-3);
  flex-wrap: wrap;
}
.app-footer__left {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.app-footer__sep { color: var(--ram-color-border); }
.app-footer__version {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
}
.app-footer__right {
  display: inline-flex;
  align-items: center;
  gap: var(--ram-space-3);
}
.app-footer__link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--ram-color-text-muted);
  text-decoration: none;
  transition: color var(--ram-duration-fast) var(--ram-ease-out);
}
.app-footer__link:hover { color: var(--ram-color-action-600); }
.app-footer__link i { font-size: 11px; }
</style>
